import { IconButton, Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import SmsCard from '@shared/components/SmsCard';

import { type ReactElement } from 'react';
import { DepositStatus, VisitDetail, VisitFinancials, VisitInfo } from '../../visits/entity';
import { FormattedMessage } from 'react-intl';
import { useRenderSmsFromTemplate } from '../useRenderSmsFromTemplate';
import { Service } from '@features/service/entity';
import { Client } from '@features/client/entity';

export const SmsList = <
  T extends { visit: VisitInfo; client: Client; services: Service[]; financials: VisitFinancials },
>({
  visitDetail,
  getText,
  icon,
  title,
}: {
  visitDetail: T[];
  getText: (visitDetail: T) => string;
  title?: string;
  icon?: ReactElement;
}) => {
  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <IconButton href="#">{icon}</IconButton>
        <Typography
          color="secondary"
          sx={{
            fontSize: '15px',
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>
      </Stack>
      {visitDetail.map(visitDetail => {
        const { visit, client, services } = visitDetail;

        return (
          <SmsCard
            dataColumnNames={{
              name: <FormattedMessage defaultMessage="Jméno" id="firstNameLabel" />,
              serviceType: <FormattedMessage defaultMessage="Účes" id="hairCut" />,
              eventType: <FormattedMessage defaultMessage="Návštěva" id="visitLabel" />,
            }}
            key={visit.id}
            phoneContact={client.phone}
            phonePrefix={client.phonePrefix}
            message={getText(visitDetail)}
            customerName={`${client.firstName} ${client.lastName}`}
            service={services.map(service => service.serviceName).join(', ')}
            daysDelta={getVisitDistanceLabel(visit.dateFrom)}
          />
        );
      })}
    </Stack>
  );
};

export function formatVisitInvitationToSms(
  lastName: string,
  services: Service[],
  visit: { date: Date },
): string {
  const { renderAppointmentReminder } = useRenderSmsFromTemplate();

  return renderAppointmentReminder({
    lastName,
    services,
    date: visit.date,
  });
}

export function formatVisitReviewRequestSms(
  lastName: string,
  reviewUrl: string | undefined,
  date: Date,
  services: Service[],
): string {
  const { renderReviewRequest } = useRenderSmsFromTemplate();

  return renderReviewRequest({
    lastName,
    reviewUrl,
    date,
    services,
  });
}

export function formatVisitPartialPaymentReminderSms(
  lastName: string,
  services: Service[],
  bankAccount: string | undefined,
  visit: {
    date: Date;
    depositRequired?: boolean | undefined;
    depositStatus: DepositStatus | null | undefined;
    depositAmount?: number | undefined;
  },
): string {
  const { date, depositRequired, depositStatus, depositAmount } = visit;
  const { renderDepositReminder } = useRenderSmsFromTemplate();

  const shouldPay = depositStatus === DepositStatus.NEZAPLACENO || depositRequired === false;

  if (shouldPay) {
    return renderDepositReminder({
      lastName,
      services,
      date,
      depositAmount,
      bankAccount,
    });
  }

  return '';
}

// Re-export capitalizeFirstLetter pro zpětnou kompatibilitu
export { capitalizeFirstLetter } from '../smsTemplateUtils';

type VisitGroups = {
  invitations: Pick<VisitDetail, 'services' | 'visit' | 'client' | 'financials'>[];
  payments: Pick<VisitDetail, 'services' | 'visit' | 'client' | 'financials'>[];
  reviews: Pick<VisitDetail, 'services' | 'visit' | 'client' | 'financials'>[];
};

export function sortAutoSms(visitDetail: VisitDetail | undefined): VisitGroups | undefined {
  if (!visitDetail) {
    return undefined;
  }

  const { visit, financials, client, services } = visitDetail;

  const now = dayjs();

  const invitations: Pick<VisitDetail, 'services' | 'visit' | 'client' | 'financials'>[] = [];
  const payments: Pick<VisitDetail, 'services' | 'visit' | 'client' | 'financials'>[] = [];
  const reviews: Pick<VisitDetail, 'services' | 'visit' | 'client' | 'financials'>[] = [];
  const visitDate = dayjs(visit.dateFrom);
  const isFuture = visitDate.isAfter(now);
  const isPast = visitDate.isBefore(now);
  const daysSinceVisit = now.diff(visitDate, 'day');

  const unpaidDeposit =
    financials.depositStatus === DepositStatus.NEZAPLACENO && client.deposit === true;

  if (isFuture) {
    invitations.push({ visit, client, services, financials });
  }

  if (unpaidDeposit && isFuture) {
    payments.push({ visit, client, services, financials });
  }

  if (isPast && daysSinceVisit <= 14) {
    reviews.push({ visit, client, services, financials });
  }

  return { invitations, payments, reviews };
}

const getVisitDistanceLabel = (visitDate: string | Date): string => {
  const today = dayjs().startOf('day');
  const visit = dayjs(visitDate).startOf('day');

  const diffDays = visit.diff(today, 'day');

  if (diffDays === 0) {
    return 'dnes';
  }
  if (diffDays > 0) {
    return `za ${diffDays} ${pluralizeDays(diffDays)}`;
  }
  return `před ${Math.abs(diffDays)} ${pluralizeDays(Math.abs(diffDays))}`;
};

const pluralizeDays = (count: number) => {
  if (count === 1) {
    return 'den';
  }
  if (count >= 2 && count <= 4) {
    return 'dny';
  }
  return 'dní';
};
